import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, Calendar, Clock, CheckCircle, XCircle, Play, Pause, RefreshCw } from 'lucide-react';
import { BriefBulletsSkandiNaviaService } from '@/services/briefBullets/briefBulletsSkandiNaviaService';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PromptViewer } from './PromptViewer';
import { BRIEFR_BULLETS_PROMPTS, SCHEDULE_INFO } from '@/constants/briefBulletsPrompts';

export const BriefBulletsSkandiNaviaManager: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const { data: taskStatus, isLoading: taskLoading } = useQuery({
    queryKey: ['briefBulletsSkandinaviaTask'],
    queryFn: () => BriefBulletsSkandiNaviaService.getTaskStatus(),
    refetchInterval: 30000,
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['briefBulletsSkandinaviaLogs'],
    queryFn: () => BriefBulletsSkandiNaviaService.getTaskLogs(),
    refetchInterval: 30000,
  });

  const publishMutation = useMutation({
    mutationFn: () => BriefBulletsSkandiNaviaService.triggerPublication(),
    onSuccess: () => {
      toast({
        title: "Publisering startet",
        description: "Skandinavia Briefr Bullets genereres nå. Dette kan ta et par minutter.",
      });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['briefBulletsSkandinaviaTask'] });
        queryClient.invalidateQueries({ queryKey: ['briefBulletsSkandinaviaLogs'] });
      }, 5000);
    },
    onError: (error) => {
      toast({
        title: "Publisering feilet",
        description: error instanceof Error ? error.message : "En ukjent feil oppstod",
        variant: "destructive",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) => BriefBulletsSkandiNaviaService.toggleAutomaticPublishing(enable),
    onSuccess: (success, enable) => {
      if (success) {
        toast({
          title: enable ? 'Aktivert' : 'Pauset',
          description: enable 
            ? 'Automatisk publisering er nå aktivert'
            : 'Automatisk publisering er nå pauset',
        });
        queryClient.invalidateQueries({ queryKey: ['briefBulletsSkandinaviaTask'] });
      } else {
        toast({
          title: 'Feil',
          description: 'Kunne ikke endre status',
          variant: 'destructive',
        });
      }
    },
  });

  const handleToggleAutoPublish = () => {
    const isActive = taskStatus?.status === 'active';
    toggleMutation.mutate(!isActive);
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;

    setIsUploadingImage(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `skandinavia-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('photos')
        .upload(filePath, imageFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('photos')
        .getPublicUrl(filePath);

      await BriefBulletsSkandiNaviaService.updatePhotoUrl(publicUrl);

      toast({
        title: "Bilde oppdatert",
        description: "Profilbildet for Skandinavia er nå oppdatert.",
      });

      setImageFile(null);
      queryClient.invalidateQueries({ queryKey: ['briefBulletsSkandinaviaTask'] });
    } catch (error) {
      toast({
        title: "Opplasting feilet",
        description: error instanceof Error ? error.message : "Kunne ikke laste opp bilde",
        variant: "destructive",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Prompt Viewer */}
      <PromptViewer 
        taskName="Skandinavia"
        prompt={BRIEFR_BULLETS_PROMPTS.skandinavia}
        config={taskStatus?.config}
        scheduleInfo={SCHEDULE_INFO.skandinavia}
      />

      <div className="bg-card rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">🏔️ Skandinavia Briefr Bullets</h3>
          <div className="flex gap-2">
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="bg-brand-primary hover:bg-brand-primary/90 text-brand-primary-foreground"
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Publiserer...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Publiser nå
                </>
              )}
            </Button>
            <Button
              onClick={handleToggleAutoPublish}
              disabled={toggleMutation.isPending}
              variant={taskStatus?.status === 'paused' ? 'default' : 'secondary'}
            >
              {toggleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Endrer...
                </>
              ) : taskStatus?.status === 'paused' ? (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Aktiver automatisk publisering
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause automatisk publisering
                </>
              )}
            </Button>
            <Button
              onClick={() => queryClient.invalidateQueries()}
              variant="outline"
              size="icon"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {taskStatus && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Søndager kl. 05:31</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>
                Neste kjøring: {taskStatus.next_run 
                  ? new Date(taskStatus.next_run).toLocaleString('nb-NO')
                  : 'Ikke planlagt'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {taskStatus.status === 'active' ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Aktiv</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span>Inaktiv</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border p-6">
        <h4 className="font-semibold mb-4">Last opp profilbilde</h4>
        <div className="space-y-4">
          {(taskStatus?.config as any)?.photo_url && (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Nåværende bilde:</p>
              <img 
                src={(taskStatus.config as any).photo_url}
                alt="Skandinavia profile" 
                className="w-32 h-32 object-cover rounded-lg border"
              />
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="flex-1"
            />
            <Button
              onClick={handleImageUpload}
              disabled={!imageFile || isUploadingImage}
              variant="outline"
            >
              {isUploadingImage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Laster opp...
                </>
              ) : (
                'Last opp'
              )}
            </Button>
          </div>
        </div>
      </div>

      {logs.length > 0 && (
        <div className="bg-card rounded-lg border p-6">
          <h4 className="font-semibold mb-4">Siste kjøringer</h4>
          <div className="space-y-2">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between p-3 bg-muted rounded-md text-sm"
              >
                <div className="flex items-center gap-2">
                  {log.status === 'completed' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : log.status === 'failed' ? (
                    <XCircle className="h-4 w-4 text-red-500" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <span className="font-medium capitalize">{log.status}</span>
                </div>
                <span className="text-muted-foreground">
                  {new Date(log.created_at).toLocaleString('nb-NO')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
